/**
 * Volume Pricing Component
 * Handles dynamic price updates when quantity changes cross bulk price break thresholds
 * 
 * This component listens to quantity changes and updates the displayed wholesale price
 * to match the applicable volume pricing tier. It also highlights the active tier
 * in the volume pricing table.
 */
if (!customElements.get('volume-pricing')) {
  customElements.define(
    'volume-pricing',
    class VolumePricing extends HTMLElement {
      constructor() {
        super();
        this.quantityInput = null;
        this.quantityChangeHandler = null;
      }

      connectedCallback() {
        // Initialize after a short delay to ensure all elements are loaded
        setTimeout(() => {
          this.init();
        }, 100);

        // Subscribe to variant changes to update volume pricing table
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const productInfo = this.closest('.product__info-container');
          if (!productInfo) return;
          
          const sectionId = productInfo.closest('[data-section]')?.dataset.section;
          if (event.data.sectionId !== sectionId) return;
          
          // Update the volume pricing display when variant changes
          this.updateVolumePricingDisplay(event.data.html);
        });

        // Subscribe to quantity updates
        this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, () => {
          // Small delay to ensure quantity value is updated
          setTimeout(() => {
            this.updatePriceBasedOnQuantity();
          }, 50);
        });
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
        if (this.quantityUpdateUnsubscriber) {
          this.quantityUpdateUnsubscriber();
        }
        if (this.quantityChangeHandler && this.quantityInput) {
          this.quantityInput.removeEventListener('change', this.quantityChangeHandler);
        }
      }

      /**
       * Initialize the component and attach event listeners
       */
      init() {
        // Find the quantity input in the product form
        const productInfo = this.closest('.product__info-container');
        if (!productInfo) return;

        this.quantityInput = productInfo.querySelector('.quantity__input');
        
        if (this.quantityInput) {
          // Create bound handler for reuse
          this.quantityChangeHandler = this.onQuantityChange.bind(this);
          
          // Listen for quantity changes
          this.quantityInput.addEventListener('change', this.quantityChangeHandler);
          this.quantityInput.addEventListener('input', this.quantityChangeHandler);
          
          // Set initial state based on current quantity
          this.updatePriceBasedOnQuantity();
        }
      }

      /**
       * Updates the displayed wholesale price when quantity changes
       */
      onQuantityChange(event) {
        this.updatePriceBasedOnQuantity();
      }

      /**
       * Main method to update price based on current quantity
       */
      updatePriceBasedOnQuantity() {
        if (!this.quantityInput) return;
        
        const quantity = parseInt(this.quantityInput.value);
        if (!quantity || quantity < 1) return;

        const priceBreakData = this.getPriceBreakData();
        if (!priceBreakData || priceBreakData.length === 0) return;

        // Find the applicable price for the current quantity
        const applicablePrice = this.getApplicablePriceForQuantity(quantity, priceBreakData);
        
        if (applicablePrice) {
          // Update the displayed price
          this.updateDisplayedPrice(applicablePrice, quantity);
        }
      }

      /**
       * Extracts price break data from the volume pricing table
       */
      getPriceBreakData() {
        const table = this.querySelector('.volume-pricing-table-display');
        if (!table) return [];

        const priceBreaks = [];
        const rows = table.querySelectorAll('tbody tr');
        
        rows.forEach(row => {
          const qtyCell = row.cells[0];
          const priceCell = row.cells[1];
          
          if (qtyCell && priceCell) {
            // Extract quantity (remove the '+' sign)
            const qtyText = qtyCell.textContent.trim().replace('+', '');
            const minQty = parseInt(qtyText);
            
            if (!isNaN(minQty)) {
              // Extract price text
              const priceText = priceCell.textContent.trim();
              
              priceBreaks.push({
                minimumQuantity: minQty,
                priceText: priceText
              });
            }
          }
        });

        return priceBreaks;
      }

      /**
       * Determines which price break applies to the given quantity
       */
      getApplicablePriceForQuantity(quantity, priceBreaks) {
        if (!priceBreaks || priceBreaks.length === 0) return null;
        
        // Sort price breaks by minimum quantity (descending) to find the highest applicable tier
        const sortedBreaks = [...priceBreaks].sort((a, b) => b.minimumQuantity - a.minimumQuantity);
        
        // Find the first price break where quantity meets or exceeds the minimum
        for (const priceBreak of sortedBreaks) {
          if (quantity >= priceBreak.minimumQuantity) {
            return priceBreak;
          }
        }

        // Fallback to the lowest tier (should always match at minimum quantity)
        return priceBreaks[0];
      }

      /**
       * Updates the main price display with the new wholesale price
       */
      updateDisplayedPrice(priceBreak, quantity) {
        const productInfo = this.closest('.product__info-container');
        if (!productInfo) return;

        // Find the wholesale price container (price-item--sale with data-price-container)
        const priceContainers = productInfo.querySelectorAll('.price-item--sale[data-price-container]');
        
        priceContainers.forEach(container => {
          // Update the wholesale price text, preserving the label if present
          const label = container.querySelector('.price-label');
          if (label) {
            // If there's a label, find and update only the first text node after it
            let foundLabel = false;
            container.childNodes.forEach(node => {
              if (node === label) {
                foundLabel = true;
              } else if (foundLabel && node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
                // Update the first text node after the label
                node.textContent = priceBreak.priceText;
                foundLabel = false; // Stop after updating the first text node
              }
            });
          } else {
            // No label, replace all text
            container.textContent = priceBreak.priceText;
          }
        });

        // Highlight the active row in the volume pricing table
        this.highlightActiveRow(priceBreak.minimumQuantity);
      }

      /**
       * Highlights the currently active price tier in the volume pricing table
       */
      highlightActiveRow(activeMinQty) {
        const table = this.querySelector('.volume-pricing-table-display');
        if (!table) return;

        const rows = table.querySelectorAll('tbody tr');
        
        rows.forEach(row => {
          const qtyCell = row.cells[0];
          if (qtyCell) {
            const qtyText = qtyCell.textContent.trim().replace('+', '');
            const minQty = parseInt(qtyText);
            
            // Add/remove highlighting
            if (minQty === activeMinQty) {
              row.style.fontWeight = '600';
              row.style.backgroundColor = '#f5f5f5';
            } else {
              row.style.fontWeight = 'normal';
              row.style.backgroundColor = 'transparent';
            }
          }
        });
      }

      /**
       * Updates the volume pricing table when variant changes
       */
      updateVolumePricingDisplay(html) {
        const productInfo = this.closest('.product__info-container');
        if (!productInfo) return;
        
        const sectionId = productInfo.closest('[data-section]')?.dataset.section;
        const volumePricingSource = html.getElementById(`Volume-${sectionId}`);
        
        if (volumePricingSource && volumePricingSource.innerHTML.trim()) {
          // Remove old event listeners before replacing HTML
          if (this.quantityChangeHandler && this.quantityInput) {
            this.quantityInput.removeEventListener('change', this.quantityChangeHandler);
            this.quantityInput.removeEventListener('input', this.quantityChangeHandler);
          }
          
          this.innerHTML = volumePricingSource.innerHTML;
          
          // Re-initialize after HTML update
          setTimeout(() => {
            this.init();
          }, 100);
        }
      }
    }
  );
}
